import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container, Form, Row, Col } from "react-bootstrap";
import { SvgIcon } from '@mui/material';
import Lock from '../icons/lock.svg?react';
import { GoogleCaptcha } from "../../Common/SubComponents/GoogleCaptcha";
import { SelectInput, TextInput, StyledRadioInput } from "../../../Helpers/Inputs";
import GetStateListForAflac from '../Helpers/StateListForAflac';
import { KENTUCKY, CALIFORNIA, CONNECTICUT, DISTRICT_OF_COLUMBIA, MINNESOTA, PENNSYLVANIA, VERMONT } from '../../../Helpers/Constants/States';
import { InputMaskPhone } from "../../../Helpers/Formatters";
import { customValidators, object, string, ref } from '../../../Helpers/Validators/customValidators';
import BackNext from '../../Common/SubComponents/BackNext';

const employmentStatusExcludedState = [CALIFORNIA, CONNECTICUT, DISTRICT_OF_COLUMBIA, MINNESOTA, PENNSYLVANIA, VERMONT];

export default function InsuredInfo(props) {

    const skipCaptcha = window.__ENV__.REACT_APP_DISABLE_CAPTCHA === 'true';

    const formMethods = useForm({
        resolver: yupResolver(InsuredInfo.ValidationSchema),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: props.formState
    });

    const { handleSubmit, trigger, setValue, register } = formMethods;

    const handleValueChange = (field, value) => {
        props.onFieldChange(field, value);
        trigger(field);
    };

    const [validCaptcha, setValidCaptcha] = useState(skipCaptcha ? true : null);

    const handleCaptchaChange = (responseToken) => {
        if (responseToken) {
            setValidCaptcha(true);
        }
    };

    useEffect(() => {
        if (props.basicInfo) {
            setValue('state', props.basicInfo.state);
            setValue('emailAddress', props.formState.emailAddress === "" ? props.basicInfo.email : props.formState.emailAddress);
            props.onStepFieldChange('state', props.basicInfo.state, 'emailAddress', props.formState.emailAddress === "" ? props.basicInfo.email : props.formState.emailAddress);
        }
    }, []);

    useEffect(() => {
        register('state');
        setValue('state', props.basicInfo.state);

        register('emailAddress');
        setValue('emailAddress', props.formState.emailAddress === "" ? props.basicInfo.email : props.formState.emailAddress);
    }, [register]);

    const onSubmit = () => {
        if (validCaptcha) {
            props.onNext();
        } else {
            setValidCaptcha(false);
        }
    };

    const getDisabled = (validCaptcha === null || props.formState.pin === '' || props.formState.pinConfirm === '' || props.formState.firstName === '' || props.formState.lastName === '' || props.formState.street === '' || props.formState.city === '' || props.formState.state === ''
        || props.formState.zip === '' || props.formState.phoneNumber === '' || props.formState.emailAddress === '' || props.formState.employer === '' ||
        (props.formState.state && !employmentStatusExcludedState.includes(props.formState.state) && props.formState.employmentStatus === '') ||
        (props.formState.state === KENTUCKY && props.formState.corporateCityLimits === ''));

    return (
        <Container fluid className='border-class'>
            <Row className="InsuredInfo justify-content-center">
                <Col>
                    <FormProvider {...formMethods}>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Row className="justify-content-center mt-4">
                                <Col lg={5}>
                                    <div className="text-center mb-2 pt-4"><SvgIcon component={Lock} className="small-icons" color="primary" fontSize="large" /></div>
                                    <div className="text-center form-title">Let's Begin. Safety first.</div>
                                    <div className="text-center mb-4"><p className="section-title">Creating a PIN will secure your personal information.</p></div>

                                    {!skipCaptcha ? (
                                        <Row className="justify-content-center">
                                            <GoogleCaptcha handleCaptchaChange={handleCaptchaChange} showError={validCaptcha === false} />
                                        </Row>
                                    ) : (
                                        <Row className="justify-content-center border my-2">
                                            <span className="text-black-50 my-2">CAPTCHA Disabled By Config</span>
                                        </Row>
                                    )}
                                    <p className="enter-pin">
                                        Enter and Confirm a PIN to be able to return to your session. All information is secure and confidential.
                                    </p>

                                    <Row>
                                        <Col sm={6} className='w-100'>
                                            <div className='mb-3'>
                                                <TextInput
                                                    type="password"
                                                    name="pin"
                                                    id="pin"
                                                    label="4 Digit Pin"
                                                    value={props.formState.pin}
                                                    onChange={e => handleValueChange('pin', e.target.value)}
                                                    inputProps={{
                                                        maxLength: 4,
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                        <Col sm={6} className='w-100'>
                                            <div className='mb-3'>
                                                <TextInput
                                                    type="password"
                                                    name="pinConfirm"
                                                    id="pinConfirm"
                                                    label="Confirm your Pin"
                                                    value={props.formState.pinConfirm}
                                                    onChange={e => handleValueChange('pinConfirm', e.target.value)}
                                                    inputProps={{
                                                        maxLength: 4,
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className='mb-3'>
                                        <TextInput
                                            name="firstName"
                                            id="firstName"
                                            label="First Name"
                                            value={props.formState.firstName}
                                            onChange={e => handleValueChange('firstName', e.target.value)}
                                            inputProps={{
                                                maxLength: 20,
                                            }}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <TextInput
                                            name="middleInitial"
                                            id="middleInitial"
                                            label="Middle Initial"
                                            value={props.formState.middleInitial}
                                            onChange={e => handleValueChange('middleInitial', e.target.value)}
                                            inputProps={{
                                                maxLength: 2,
                                            }}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <TextInput
                                            name="lastName"
                                            id="lastName"
                                            label="Last Name"
                                            value={props.formState.lastName}
                                            onChange={e => handleValueChange('lastName', e.target.value)}
                                            inputProps={{
                                                maxLength: 50,
                                            }}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <p className="section-title">Street Address</p>
                                        <TextInput
                                            name="street"
                                            id="street"
                                            label="Street Address"
                                            value={props.formState.street}
                                            onChange={e => handleValueChange('street', e.target.value)}
                                            inputProps={{
                                                maxLength: 40,
                                            }}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <TextInput
                                            name="city"
                                            id="city"
                                            label="City"
                                            value={props.formState.city}
                                            onChange={e => handleValueChange('city', e.target.value)}
                                            inputProps={{
                                                maxLength: 40,
                                            }}
                                        />
                                    </div>

                                    <Row>
                                        <Col>
                                            <div className='mb-3'>
                                                <SelectInput
                                                    name="state"
                                                    id="state"
                                                    value={props.formState.state}
                                                    onChange={(e) => handleValueChange('state', e.target.value)}
                                                    label="State"
                                                    selectOptions={GetStateListForAflac()}
                                                    disabled={true}
                                                />
                                            </div>
                                        </Col>
                                        <Col>
                                            <div className='mb-3'>
                                                <TextInput
                                                    name="zip"
                                                    id="zip"
                                                    label="Zip"
                                                    value={props.formState.zip}
                                                    onChange={e => handleValueChange('zip', e.target.value)}
                                                    inputProps={{
                                                        maxLength: 5,
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    {props.formState.state === KENTUCKY &&
                                        <div className='mb-3'>
                                            <p className="section-title">Do you live in the corporate city limits?</p>
                                            <StyledRadioInput
                                                name="corporateCityLimits"
                                                id="corporateCityLimits"
                                                value={props.formState.corporateCityLimits}
                                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                                onChange={e => handleValueChange('corporateCityLimits', e)}
                                                isWhiteBackground={true}
                                                isBlankIcon={true}
                                            />
                                        </div>
                                    }

                                    <div className='mb-3'>
                                        <TextInput
                                            name="phoneNumber"
                                            id="phoneNumber"
                                            label="Primary Phone Number"
                                            value={props.formState.phoneNumber}
                                            onChange={e => handleValueChange('phoneNumber', e.target.value)}
                                            InputProps={{
                                                inputComponent: InputMaskPhone,
                                            }}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <TextInput
                                            name="emailAddress"
                                            id="emailAddress"
                                            label="Email"
                                            value={props.formState.emailAddress}
                                            onChange={e => handleValueChange('emailAddress', e.target.value)}
                                            inputProps={{
                                                maxLength: 50,
                                            }}
                                        />
                                    </div>

                                    <div className='mb-3'>
                                        <TextInput
                                            name="employer"
                                            id="employer"
                                            label="Employer"
                                            value={props.formState.employer}
                                            onChange={e => handleValueChange('employer', e.target.value)}
                                            inputProps={{
                                                maxLength: 100,
                                            }}
                                        />
                                    </div>

                                    {props.formState.state && !employmentStatusExcludedState.includes(props.formState.state) &&
                                        <div className='mb-3'>
                                            <p className="section-title">Are you, the Proposed Insured, currently reporting to work (not out on leave, FML, disability, hiatus, or layoff) with the employer listed on this application?</p>
                                            <StyledRadioInput
                                                name="employmentStatus"
                                                id="employmentStatus"
                                                value={props.formState.employmentStatus}
                                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                                onChange={e => handleValueChange('employmentStatus', e)}
                                                isWhiteBackground={true}
                                                isBlankIcon={true}
                                            />
                                        </div>
                                    }

                                </Col>
                            </Row>

                            <div className='backnext-wrapper'>
                                <BackNext onBack={props.onBack} isAflac={true} continueLabel={{ text: "Next" }} nextDisabled={getDisabled} />
                            </div>
                        </Form>
                    </FormProvider>
                </Col>
            </Row>
        </Container>
    );
}

InsuredInfo.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func,
    onFieldChange: PropTypes.func.isRequired,
    onBack: PropTypes.func,
    basicInfo: PropTypes.object,
};

InsuredInfo.ValidationSchema = object().shape({
    pin: string()
        .required('PIN is required.')
        .length(4)
        .matches(/\d{4}/, 'PIN must be 4 numeric digits.'),
    pinConfirm: string()
        .required('PIN confirm is required.')
        .oneOf([ref('pin')], 'PINs must match.'),
    firstName: customValidators.string().isValidNameWithFiftyChar('First name is required.', true),
    middleInitial: customValidators.string().isValidNameWithFiftyChar('Middle Initials is required', false),
    lastName: customValidators.string().isValidNameWithFiftyChar('Last name is required.', true),
    street: customValidators.string().isValidNameLimitedSpecialChar('Street is required.', true, true),
    city: string().isValidNameLimitedSpecialChar('City is required.', true, false),
    state: string().required("State is required."),
    zip: string().required("Zip is required").matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
    corporateCityLimits: string().when('state', {
        is: "KY",
        then: (schema) => schema.required('This field is required.'),
        otherwise: (schema) => schema.notRequired()
    }),
    phoneNumber: string()
        .required('Phone Number is required.')
        .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
    emailAddress: string()
        .email('Invalid email')
        .required('Email is required.'),
    employer: string().required("Employer is required."),
    employmentStatus: string().when('state', {
        is: (state) => state && !employmentStatusExcludedState.includes(state),
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
    })
});

InsuredInfo.DefaultFormState = (prefill = false, basicInfo = null) => {
    return {
        pin: prefill ? "1111" : "",
        pinConfirm: prefill ? "1111" : "",
        firstName: prefill ? "Proposed" : (basicInfo && basicInfo.firstName) || '',
        middleInitial: prefill ? "N" : '',
        lastName: prefill ? "Sured" : (basicInfo && basicInfo.lastName) || '',
        street: prefill ? "2020 Covr St" : '',
        city: prefill ? "Boise" : '',
        state: prefill ? "CA" : (basicInfo && basicInfo.state) || '',
        zip: prefill ? "83702" : '',
        corporateCityLimits: '',
        phoneNumber: prefill ? "(769) 345-4567" : (basicInfo && basicInfo.phone) || '',
        emailAddress: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
        employer: prefill ? "Employer" : '',
        employmentStatus: ''
    };
};
